import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Box,
  ChevronRight,
  Eye,
  EyeOff,
  RefreshCw,
  Server,
  Terminal,
} from 'lucide-react';
import { useCustomInstance, useCustomInstanceSocketInvalidation } from '../hooks/useCustomInstances';
import type { CustomParameterValue } from '../types/custom';
import { customErrorMessage, formatBytes } from '../utils/customUi';
import { customInstanceListSearch, readCustomInstanceListParams } from '../utils/customInstanceSearch';
import { CustomInstanceActions, CustomInstanceStatus } from '../components/admin/custom/CustomInstanceActions';
import { CustomPersistentVolumes } from '../components/admin/custom/CustomPersistentVolumes';
import { CustomRuntimeFiles } from '../components/admin/custom/CustomRuntimeFiles';
import { CustomConsoleModal } from '../components/admin/custom/CustomConsoleModal';

export function CustomInstanceDetailPage() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [searchParams] = useSearchParams();
  const [notice, setNotice] = useState('');
  const [consoleOpen, setConsoleOpen] = useState(false);
  const detailQuery = useCustomInstance(instanceId);
  useCustomInstanceSocketInvalidation(instanceId);

  const indexSearch = useMemo(
    () => customInstanceListSearch(readCustomInstanceListParams(searchParams)),
    [searchParams],
  );
  const instance = detailQuery.data?.instance;

  if (detailQuery.isLoading) {
    return <div className="max-w-7xl mx-auto px-4 py-12"><div className="spinner w-10 h-10 mx-auto" /></div>;
  }

  if (detailQuery.isError || !instance) {
    return <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="empty-state">
        <Server className="empty-state-icon text-red-400" />
        <div className="empty-state-title">Custom instance unavailable</div>
        <p className="empty-state-text">{customErrorMessage(detailQuery.error, 'The custom instance could not be loaded.')}</p>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => void detailQuery.refetch()}><RefreshCw className="w-4 h-4" /> Retry</button>
          <Link className="btn-primary" to={`/admin/custom-instances${indexSearch}`}>Back to index</Link>
        </div>
      </div>
    </div>;
  }

  const pinned = instance.pinned;
  const observed = instance.observed;
  const productAdminSearch = new URLSearchParams({
    tab: 'custom-products',
    productId: pinned.product.id,
    section: 'instances',
  }).toString();
  const missingMetadata = [pinned.product, pinned.image, pinned.replica, pinned.agent]
    .some(item => item.missing) || pinned.revision.missing;

  return <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
    <nav className="flex flex-wrap items-center gap-1 text-xs text-gray-500" aria-label="Breadcrumb">
      <Link className="hover:text-neon-300" to="/admin">Admin</Link><ChevronRight className="w-3 h-3" />
      <Link className="hover:text-neon-300" to={`/admin/custom-instances${indexSearch}`}>Custom Instances</Link><ChevronRight className="w-3 h-3" />
      {pinned.product.missing
        ? <span className="text-amber-300">{pinned.product.displayName || pinned.product.key || pinned.product.id}</span>
        : <Link className="hover:text-neon-300" to={`/admin?${productAdminSearch}`}>
          {pinned.product.displayName || pinned.product.key || pinned.product.id}
        </Link>}<ChevronRight className="w-3 h-3" />
      <span className="text-gray-300">{instance.name}</span>
    </nav>

    <div className="flex flex-wrap items-start gap-4">
      <Link className="btn-ghost p-2" title="Back to filtered index" to={`/admin/custom-instances${indexSearch}`}><ArrowLeft className="w-5 h-5" /></Link>
      <div className="w-12 h-12 rounded-xl bg-neon-500/10 flex items-center justify-center"><Box className="w-6 h-6 text-neon-400" /></div>
      <div className="flex-1 min-w-52">
        <div className="flex flex-wrap items-center gap-3"><h1 className="text-2xl font-bold text-gray-100">{instance.name}</h1><CustomInstanceStatus status={observed.actualState} /></div>
        <p className="text-xs text-gray-500 mt-1"><code>{instance.id}</code> · updated {formatTimestamp(instance.updatedAt)}</p>
      </div>
      <button className="btn-secondary" onClick={() => void detailQuery.refetch()}><RefreshCw className={`w-4 h-4 ${detailQuery.isFetching ? 'animate-spin' : ''}`} /> Refresh from Mothership</button>
    </div>

    {notice && <div className="p-3 rounded border border-neon-500/20 bg-neon-500/10 text-sm text-neon-300">{notice}</div>}
    {missingMetadata && <div className="p-4 rounded-lg border border-amber-500/25 bg-amber-500/10 text-sm text-amber-200 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 shrink-0" />
      <span>One or more catalog joins are unavailable. The immutable IDs, hashes and launch snapshot below remain authoritative; no current revision was substituted.</span>
    </div>}

    <section className="space-y-4" aria-labelledby="pinned-launch-title">
      <div>
        <h2 id="pinned-launch-title" className="section-title">Pinned launch configuration</h2>
        <p className="section-subtitle mt-1">Immutable intent resolved once by Mothership when this instance was created.</p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        <InfoCard title="Product" rows={[
          ['Name', pinned.product.displayName || pinned.product.key || 'Metadata unavailable'],
          ['Product ID', pinned.product.id],
          ['Revision', `r${pinned.revision.number}${pinned.revision.missing ? ' · metadata unavailable' : ''}`],
          ['Definition hash', pinned.definitionHash],
        ]} />
        <InfoCard title="Image release" rows={[
          ['Version', pinned.image.version || 'Metadata unavailable'],
          ['Image ID', pinned.image.id],
          ['Replica ID', pinned.replica.id],
          ['Image tag', pinned.imageTag],
        ]} />
        <InfoCard title="Pinned Docker image" rows={[
          ['Docker image ID', pinned.dockerImageId],
          ['Repo digest', pinned.dockerRepoDigest || 'Not reported'],
          ['Spec hash', pinned.specHash],
          ['Replica match', pinned.replica.matchesPinned ? 'yes' : 'no'],
        ]} />
        <InfoCard title="Placement" rows={[
          ['Agent', pinned.agent.name || pinned.agent.id],
          ['Address', pinned.agent.address || pinned.agent.ipAddress || 'Unavailable'],
          ['Region', pinned.agent.region || 'Not set'],
          ['Created', formatTimestamp(instance.createdAt)],
        ]} />
      </div>

      <div className="grid xl:grid-cols-2 gap-4">
        <div className="card p-5">
          <h3 className="font-semibold text-gray-200">Launch parameters</h3>
          <p className="text-xs text-gray-500 mt-1">Labels and types come from the pinned revision. Sensitive values require an explicit reveal.</p>
          <div className="mt-4 divide-y divide-dark-700">
            {pinned.parameterValues.length ? pinned.parameterValues.map(parameter => <div key={parameter.key} className="py-3 grid sm:grid-cols-[minmax(9rem,0.8fr)_minmax(0,1.2fr)] gap-3">
              <div><div className="text-sm text-gray-300">{parameter.label}</div><div className="text-xs text-gray-600 mt-1">{parameter.key} · {parameter.type}</div>{parameter.description && <div className="text-xs text-gray-500 mt-1">{parameter.description}</div>}</div>
              <SensitiveValue value={parameter.value} sensitive={parameter.sensitive} label={parameter.label} />
            </div>) : <div className="py-4 text-sm text-gray-500">No launch parameters were pinned.</div>}
          </div>
        </div>

        <div className="card p-5">
          <h3 className="font-semibold text-gray-200">Process</h3>
          <div className="grid sm:grid-cols-2 gap-3 mt-4 text-xs">
            <LabelValue label="Working directory" value={pinned.workingDirectory} code />
            <LabelValue label="Runtime files root" value={pinned.runtimeFilesRoot} code />
            <LabelValue label="Restart policy" value={pinned.restartPolicy} />
            <LabelValue label="Stop timeout" value={`${pinned.stopTimeoutSeconds}s`} />
            <LabelValue label="Readiness" value={pinned.readiness ? `${pinned.readiness.type} /${pinned.readiness.pattern}/${pinned.readiness.flags || ''}` : 'Not declared'} />
          </div>
          <h4 className="text-xs uppercase tracking-wider font-semibold text-gray-500 mt-5">Resolved argv</h4>
          <div className="mt-2 space-y-2 max-h-72 overflow-auto">{pinned.argv.map(argument => <div key={argument.index} className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-2 items-center"><span className="text-xs text-gray-600 text-right">[{argument.index}]</span><SensitiveValue value={argument.value} sensitive={argument.sensitive} label={`argv ${argument.index}`} /></div>)}</div>
        </div>

        <div className="card p-5">
          <h3 className="font-semibold text-gray-200">Resolved environment</h3>
          <div className="mt-4 divide-y divide-dark-700 max-h-96 overflow-auto">
            {pinned.environment.length ? pinned.environment.map(entry => <div key={entry.name} className="py-3 grid sm:grid-cols-[minmax(8rem,0.7fr)_minmax(0,1.3fr)] gap-3 items-center"><code className="text-xs text-cyber-400 break-all">{entry.name}</code><SensitiveValue value={entry.value} sensitive={entry.sensitive} label={`environment ${entry.name}`} /></div>) : <div className="py-4 text-sm text-gray-500">No environment variables were pinned.</div>}
          </div>
        </div>

        <div className="card p-5">
          <h3 className="font-semibold text-gray-200">Resources</h3>
          <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
            <LabelValue label="Profile" value={pinned.resources.profileKey || 'Pinned custom profile'} />
            <LabelValue label="CPU" value={`${pinned.resources.cpuCores} cores · ${pinned.resources.nanoCpus} NanoCPUs`} />
            <LabelValue label="RAM" value={formatBytes(pinned.resources.memoryBytes)} />
            <LabelValue label="Storage" value={formatBytes(pinned.resources.storageBytes)} />
          </div>
          <h4 className="text-xs uppercase tracking-wider font-semibold text-gray-500 mt-5">Operation IDs</h4>
          <div className="mt-2 space-y-2"><LabelValue label="Prepare" value={pinned.operationIds.prepare} code />{pinned.operationIds.lifecycle && <LabelValue label="Lifecycle" value={pinned.operationIds.lifecycle} code />}{pinned.operationIds.restartStart && <LabelValue label="Restart start" value={pinned.operationIds.restartStart} code />}</div>
        </div>
      </div>

      <div className="grid xl:grid-cols-2 gap-4">
        <div className="card p-5"><h3 className="font-semibold text-gray-200">Ports and endpoints</h3><div className="mt-4 overflow-x-auto"><table className="w-full text-xs"><thead className="text-gray-500"><tr><th className="text-left py-2">Key</th><th className="text-left py-2">Container</th><th className="text-left py-2">Host</th><th className="text-left py-2">Address</th></tr></thead><tbody className="divide-y divide-dark-700">{pinned.ports.map(port => <tr key={`${port.key}:${port.protocol}`}><td className="py-3 text-gray-300">{port.key}</td><td className="py-3 text-gray-400">{port.containerPort}/{port.protocol}</td><td className="py-3 text-gray-400">{port.hostPort}/{port.protocol}</td><td className="py-3 text-gray-300 break-all">{port.address || 'Unavailable'}</td></tr>)}</tbody></table>{pinned.ports.length === 0 && <div className="py-4 text-gray-500">No ports declared.</div>}</div></div>
        <div className="card p-5"><h3 className="font-semibold text-gray-200">Persistent mounts</h3><div className="mt-4 divide-y divide-dark-700">{pinned.mounts.map(mount => <div key={mount.key} className="py-3 flex justify-between gap-3 text-xs"><div><div className="text-gray-300">{mount.key}</div><code className="text-gray-500">{mount.containerPath}</code></div><span className={mount.readOnly ? 'badge-warning' : 'badge-online'}>{mount.readOnly ? 'read-only' : 'read-write'}</span></div>)}{pinned.mounts.length === 0 && <div className="py-4 text-sm text-gray-500">No persistent mounts declared.</div>}</div></div>
      </div>
    </section>

    <CustomPersistentVolumes instanceId={instance.id} />
    <CustomRuntimeFiles instanceId={instance.id} />

    <section className="space-y-4" aria-labelledby="observed-state-title">
      <div>
        <h2 id="observed-state-title" className="section-title">Operational and observed state</h2>
        <p className="section-subtitle mt-1">Desired state is Mothership intent. Actual state and runtime observation are the last persisted Docker evidence.</p>
      </div>
      <div className="card p-5">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
            <LabelValue label="Desired (Mothership)" value={observed.desiredState} />
            <div><div className="text-xs text-gray-600">Actual (last observed)</div><div className="mt-1"><CustomInstanceStatus status={observed.actualState} /></div></div>
            <LabelValue label="Preparation" value={`${observed.preparation.phase} · attempt ${observed.preparation.attempts}`} />
            <LabelValue label="Last observed" value={formatTimestamp(observed.lastObservedAt || observed.runtimeObservation?.observedAt)} />
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 items-center">
              <CustomInstanceActions instance={{ id: instance.id, name: instance.name, actualState: observed.actualState, preparation: observed.preparation, lifecycleOperation: observed.lifecycleOperation }} onAccepted={setNotice} />
              <button className="btn-secondary" disabled={!instance.managementConsole?.available} onClick={() => setConsoleOpen(true)}
                title={instance.managementConsole?.available ? 'Open management console' : (instance.managementConsole?.reason || 'This pinned revision does not declare a supported console.')}>
                <Terminal className="w-4 h-4" /> Console
              </button>
            </div>
            {!instance.managementConsole?.available && <p className="text-xs text-gray-500 max-w-lg">{instance.managementConsole?.reason || 'This pinned revision does not declare a supported console.'}</p>}
          </div>
        </div>
        {(observed.lastError || observed.preparation.lastError || observed.lifecycleOperation?.lastError) && <div className="mt-5 p-3 rounded border border-red-500/20 bg-red-500/10 text-sm text-red-300 break-words">{observed.lastError || observed.lifecycleOperation?.lastError || observed.preparation.lastError}</div>}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        <InfoCard title="Observed Agent" rows={[
          ['Name', observed.agent.name || observed.agent.id],
          ['Address', observed.agent.address || observed.agent.ipAddress || 'Unavailable'],
          ['Connected', observed.agent.connected === undefined ? 'Unknown' : (observed.agent.connected ? 'yes' : 'no')],
          ['Metadata', observed.agent.missing ? 'Unavailable' : 'Available'],
        ]} />
        <InfoCard title="Container identity" rows={[
          ['Container name', observed.containerName],
          ['Docker ID', observed.dockerId || observed.runtimeObservation?.dockerId || 'Not observed'],
          ['Docker image ID', observed.dockerImageId],
        ]} />
        <InfoCard title="Runtime observation" rows={observed.runtimeObservation ? [
          ['Status', observed.runtimeObservation.status],
          ['Phase', observed.runtimeObservation.phase],
          ['Observed at', formatTimestamp(observed.runtimeObservation.observedAt)],
        ] : [['Status', 'No runtime observation persisted']]} />
        <InfoCard title="Lifecycle" rows={observed.lifecycleOperation ? [
          ['Type', observed.lifecycleOperation.type],
          ['Phase', observed.lifecycleOperation.phase],
          ['Attempts', String(observed.lifecycleOperation.attempts)],
          ['Updated', formatTimestamp(observed.lifecycleOperation.completedAt || observed.lifecycleOperation.lastAttemptAt || observed.updatedAt)],
        ] : [['Operation', 'No lifecycle operation']]} />
      </div>
    </section>
    {consoleOpen && <CustomConsoleModal key={instance.id} instance={instance} onClose={() => setConsoleOpen(false)} />}
  </div>;
}

function SensitiveValue({
  value,
  sensitive,
  label,
}: {
  value: CustomParameterValue;
  sensitive: boolean;
  label: string;
}) {
  const [revealed, setRevealed] = useState(false);
  const rendered = typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value);
  return <div className="min-w-0 flex items-center gap-2">
    <code className="min-w-0 flex-1 p-2 rounded bg-black/30 text-xs text-gray-300 break-all">
      {sensitive && !revealed ? '••••••••' : rendered}
    </code>
    {sensitive && <button
      type="button"
      className="btn-ghost p-2 shrink-0"
      aria-label={`${revealed ? 'Hide' : 'Reveal'} ${label}`}
      onClick={() => setRevealed(current => !current)}
    >{revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>}
  </div>;
}

function LabelValue({ label, value, code = false }: { label: string; value: string; code?: boolean }) {
  return <div className="min-w-0"><div className="text-xs text-gray-600">{label}</div>{code ? <code className="block text-xs text-gray-300 mt-1 break-all">{value}</code> : <div className="text-sm text-gray-300 mt-1 break-words">{value}</div>}</div>;
}

function InfoCard({ title, rows }: { title: string; rows: Array<[string, string]> }) {
  return <div className="card p-4 min-w-0"><h3 className="text-xs uppercase tracking-wider font-semibold text-gray-500">{title}</h3><div className="mt-3 space-y-3">{rows.map(([label, value]) => <LabelValue key={label} label={label} value={value} code={/ID|hash|digest|tag|name/i.test(label)} />)}</div></div>;
}

function formatTimestamp(value?: string): string {
  if (!value) return 'Not observed';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}
